/**
 * Vector diagrams, drawn in code.
 *
 * The reference draws its illustrations as inline SVG in the page, in the
 * project palette, with a `<title>` for anyone who cannot see them. These are
 * the same idea made reusable: a step names a diagram and gets it in the column
 * where a photograph would otherwise sit.
 *
 * Drawn here rather than uploaded for two reasons. They are content that has to
 * stay correct if the story changes, so they belong in version control next to
 * the copy. And an SVG pasted into a CMS field is markup from a text box, which
 * is the shape of every stored-XSS bug ever written: a diagram chosen by key
 * cannot be anything other than one of these.
 *
 * Palette, from DESIGN.md: navy #1e2a6b for line work, orange #f5931f for the
 * tape, bubble blue #2f6bff for dimensions and labels, red #e0322b for printed
 * parts. Numbers are set in the mono face, as they are on the rest of the page.
 */

const NAVY = '#1e2a6b'
const ORANGE = '#f5931f'
const BLUE = '#2f6bff'
const RED = '#e0322b'
const INK = '#141821'
const MONO = 'JetBrains Mono, ui-monospace, monospace'

interface Diagram {
  /** Read to anyone who cannot see the drawing. Also the prose fallback. */
  title: string
  viewBox: string
  markup: string
}

/** A double-ended arrowhead, as a marker.
 *
 *  `auto-start-reverse` is what makes one definition serve both ends: without
 *  it the start marker points the way the path is going, which on a
 *  double-headed measurement arrow is backwards. */
function arrowhead(id: string, colour: string): string {
  return (
    `<marker id="${id}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="4.5" ` +
    `markerHeight="4.5" orient="auto-start-reverse">` +
    `<path d="M0 0.5L10 5L0 9.5z" fill="${colour}"/></marker>`
  )
}

const DIAGRAMS: Record<string, Diagram> = {
  /** The three tape numbers. Two views, because one cannot show all three. */
  'head-measurements': {
    title:
      'A head seen from a raised three-quarter angle, with the three measurements ' +
      'drawn as curved double-headed arrows lying on its surface. Orange runs all ' +
      'the way round the brow for the circumference. Blue runs over the crown from ' +
      'the forehead to the back of the head. Red runs over the crown from one ear ' +
      'to the other. Where an arrow passes behind the head it is drawn faintly.',
    viewBox: '0 0 440 330',
    markup: `
      <defs>
        <linearGradient id="bm-skin" x1="0.15" y1="0" x2="0.7" y2="1">
          <stop offset="0" stop-color="#ffffff"/>
          <stop offset="1" stop-color="#e7ecf7"/>
        </linearGradient>
        ${arrowhead('bm-circ', ORANGE)}
        ${arrowhead('bm-fb', BLUE)}
        ${arrowhead('bm-ss', RED)}
      </defs>

      <!-- ── The head ─────────────────────────────────────────────────────
           Everything in this drawing comes out of one orthographic projection
           of a sphere, turned 34 degrees and tilted 24, then scaled taller than
           it is wide. That is why the arrows sit ON the head rather than
           ballooning off it: the silhouette and the three arcs are the same
           projection, so they cannot disagree with each other. -->
      <!-- The cranium follows the projected sphere exactly, because that is
           where all three arrows lie. Below the brow it narrows into a jaw and
           a chin: a perfect ellipse reads as a ball, and a ball has no front,
           which would make every arrow below ambiguous. -->
      <path d="M108 170A92 108 0 0 1 292 170C292 214 277 249 241 268C210 283 167 269 139 231C119 207 108 189 108 170Z"
            fill="url(#bm-skin)" stroke="${NAVY}" stroke-width="2.5" stroke-linejoin="round"/>

      <!-- Both ears, at their projected positions. The near one is drawn
           solid; the far one is ghosted like everything else on the back of
           the head, and exists so the red arrow's far head lands on something
           rather than stopping in mid-air. -->
      <path d="M274.1 136.7c9.4-2.2 13.8 7.2 9.4 17.3s-11.6 8-13 1.4" fill="none"
            stroke="${NAVY}" stroke-width="1.8" stroke-dasharray="4 5" opacity=".35" stroke-linejoin="round"/>
      <path d="M125.9 182.6c-12.4-2.9-18.1 9.5-12.4 22.8s15.2 10.5 17.1 1.9" fill="url(#bm-skin)" stroke="${NAVY}" stroke-width="2.2" stroke-linejoin="round"/>
      <path d="M123.9 192.1c-4.8 1.9-5.7 8.6-1.9 12.4" fill="none" stroke="${NAVY}" stroke-width="1.4" opacity=".5" stroke-linecap="round"/>
      <!-- Nose and jawline, on the surface rather than breaking the outline:
           at this angle the face turns towards the viewer, not past them. -->
      <path d="M256 196c11 6 14 15 4 19" fill="none" stroke="${NAVY}" stroke-width="2.2" stroke-linecap="round"/>
      <path d="M247 234c-6 12-20 21-38 23" fill="none" stroke="${NAVY}" stroke-width="1.4" opacity=".4" stroke-linecap="round"/>

      <!-- ── 1. Round the brow ────────────────────────────────────────────
           The plane of this loop is z = 0.20 on the unit sphere, which puts it
           just clear of the ear. It is where a tape would actually sit, and it
           is also the datum the other two arcs are cut against. -->
      <path d="M274.7 126.2L277.9 128.6L280.7 131.1L283.1 133.6L285.2 136.3L287.0 138.9L288.3 141.7L289.3 144.5L110.6 144.8L111.5 142.1L112.8 139.3L114.5 136.6L116.6 134.0L119.0 131.4L121.7 128.9L124.8 126.5L128.2 124.2L132.0 122.0L136.0 120.0L140.3 118.0L144.8 116.2L149.6 114.6L154.6 113.1L159.8 111.7L165.1 110.6L170.7 109.6L176.3 108.7L182.0 108.1L187.8 107.6L193.7 107.3L199.6 107.2L205.5 107.3L211.4 107.6L217.2 108.0L223.0 108.6L228.6 109.5L234.1 110.4L239.5 111.6L244.7 112.9L249.8 114.4L254.6 116.0L259.1 117.8L263.5 119.7L267.5 121.7L271.3 123.9L274.7 126.2"
            fill="none" stroke="${ORANGE}" stroke-width="3" stroke-dasharray="5 6" opacity=".4"/>
      <path d="M289.9 147.3L290.1 150.1L290.0 152.9L289.4 155.7L288.5 158.5L287.2 161.2L285.5 163.9L283.4 166.6L281.0 169.1L278.3 171.6L275.2 174.0L271.8 176.3L268.0 178.5L264.0 180.6L259.7 182.5L255.2 184.3L250.4 185.9L245.4 187.4L240.2 188.8L234.9 190.0L229.3 191.0L223.7 191.8L218.0 192.4L212.2 192.9L206.3 193.2L200.4 193.3L194.5 193.2L188.6 193.0L182.8 192.5L177.0 191.9L171.4 191.1L165.9 190.1L160.5 189.0L155.3 187.6L150.2 186.2L145.4 184.5L140.9 182.8L136.5 180.8L132.5 178.8L128.7 176.6L125.3 174.3L122.1 171.9L119.3 169.5L116.9 166.9L114.8 164.3L113.0 161.6L111.7 158.8L110.7 156.1L110.1 153.3L109.9 150.5L110.0 147.6"
            fill="none" stroke="${ORANGE}" stroke-width="3.6" stroke-linecap="butt" stroke-linejoin="round"
            marker-start="url(#bm-circ)" marker-end="url(#bm-circ)"/>

      <!-- ── 2. Front to back, over the crown ─────────────────────────────
           Clipped to the brow plane at both ends. A tape run over the crown
           stops where the circumference tape crosses it; running on down the
           forehead would be measuring something nobody measured. -->
      <path d="M174.7 66.2L172.8 67.0L171.0 68.0L169.2 69.2L167.5 70.5L165.8 72.1L164.2 73.8L162.6 75.7L161.2 77.8L159.7 80.1L158.4 82.5L157.2 85.1L156.0 87.8L154.9 90.7L153.9 93.7L152.9 96.9L152.1 100.2L151.3 103.6L150.7 107.1L150.1 110.8L149.6 114.6"
            fill="none" stroke="${BLUE}" stroke-width="3" stroke-dasharray="5 6" opacity=".4"
            marker-end="url(#bm-fb)"/>
      <path d="M250.4 185.9L249.9 181.5L249.3 177.0L248.7 172.5L247.9 168.0L247.1 163.5L246.1 159.0L245.1 154.6L244.0 150.1L242.8 145.7L241.6 141.4L240.3 137.1L238.8 132.8L237.4 128.6L235.8 124.5L234.2 120.5L232.5 116.6L230.8 112.8L229.0 109.1L227.2 105.4L225.3 102.0L223.3 98.6L221.4 95.3L219.3 92.2L217.3 89.3L215.2 86.5L213.1 83.8L210.9 81.3L208.8 79.0L206.6 76.8L204.4 74.8L202.2 73.0L200.0 71.3L197.8 69.9L195.6 68.6L193.4 67.5L191.2 66.6L189.1 65.9L186.9 65.3L184.8 65.0L182.7 64.8L180.7 64.9L178.6 65.1L176.7 65.6"
            fill="none" stroke="${BLUE}" stroke-width="3.6" stroke-linecap="butt" stroke-linejoin="round"
            marker-start="url(#bm-fb)"/>

      <!-- ── 3. Ear to ear, over the crown ────────────────────────────────
           This one does not stop at the brow: its ends are the ears, which sit
           below that plane. The near end runs a fraction inside the ear so the
           arrowhead lands on it. -->
      <path d="M249.2 78.8L252.0 81.0L254.6 83.5L257.1 86.2L259.5 89.1L261.7 92.2L263.8 95.4L265.8 98.8L267.5 102.4L269.2 106.2L270.6 110.1L272.0 114.1L273.1 118.3L274.1 122.5L274.9 126.9L275.5 131.4L275.9 136.0L276.2 140.7L276.3 145.4"
            fill="none" stroke="${RED}" stroke-width="3" stroke-dasharray="5 6" opacity=".4"
            marker-end="url(#bm-ss)"/>
      <path d="M123.8 189.6L124.1 184.8L124.5 179.9L125.2 175.0L126.0 170.1L126.9 165.2L128.1 160.3L129.4 155.4L130.9 150.6L132.5 145.8L134.3 141.0L136.3 136.3L138.4 131.8L140.6 127.2L143.0 122.8L145.5 118.5L148.1 114.4L150.9 110.3L153.8 106.4L156.7 102.7L159.8 99.1L163.0 95.6L166.3 92.4L169.6 89.3L173.0 86.4L176.5 83.7L180.0 81.2L183.6 78.9L187.2 76.8L190.9 75.0L194.5 73.3L198.2 71.9L201.9 70.8L205.6 69.8L209.3 69.1L212.9 68.6L216.5 68.4L220.1 68.4L223.6 68.6L227.1 69.0L230.5 69.7L233.8 70.7L237.1 71.8L240.3 73.2L243.4 74.9L246.3 76.7"
            fill="none" stroke="${RED}" stroke-width="3.6" stroke-linecap="butt" stroke-linejoin="round"
            marker-start="url(#bm-ss)"/>

      <!-- ── Labels, each in its own arrow's colour ───────────────────────-->
      <path d="M291 150h18" stroke="${ORANGE}" stroke-width="1.4" stroke-dasharray="3 4"/>
      <text x="313" y="147" font-family="${MONO}" font-size="11" fill="${ORANGE}">circumference</text>
      <text x="313" y="160" font-family="${MONO}" font-size="9" fill="${ORANGE}" opacity=".85">all the way round the brow</text>

      <path d="M180 63l-28-22" stroke="${BLUE}" stroke-width="1.4" stroke-dasharray="3 4"/>
      <text x="148" y="34" text-anchor="end" font-family="${MONO}" font-size="11" fill="${BLUE}">front to back</text>
      <text x="148" y="47" text-anchor="end" font-family="${MONO}" font-size="9" fill="${BLUE}" opacity=".85">over the crown</text>

      <path d="M119 204l-25 26" stroke="${RED}" stroke-width="1.4" stroke-dasharray="3 4"/>
      <text x="90" y="245" text-anchor="end" font-family="${MONO}" font-size="11" fill="${RED}">side to side</text>
      <text x="90" y="258" text-anchor="end" font-family="${MONO}" font-size="9" fill="${RED}" opacity=".85">ear to ear</text>
    `,
  },

  /** The modelling itself: slices at intervals, swept between. */
  'oval-slices': {
    title:
      'The modelled dome as a stack of oval slices seen from a raised angle, with ' +
      'straight lines swept down the sides between one slice and the next. The ' +
      'segments are straight, which is why the finished surface is faceted rather ' +
      'than smooth. Only the upper half of the head is modelled; the widest slice ' +
      'at the bottom is the brow.',
    viewBox: '0 0 460 200',
    markup: `
      <g fill="none" stroke="${BLUE}" stroke-width="1.9">
        <ellipse cx="175" cy="60" rx="24.4" ry="6.3" opacity=".45"/>
        <ellipse cx="175" cy="77" rx="52.9" ry="13.8" opacity=".55"/>
        <ellipse cx="175" cy="98" rx="69.3" ry="18.0" opacity=".68"/>
        <ellipse cx="175" cy="119" rx="77.5" ry="20.2" opacity=".8"/>
        <ellipse cx="175" cy="140" rx="80.0" ry="20.8" opacity=".9"/>
      </g>
      <!-- Swept between: straight from one slice edge to the next, which is the
           whole point. The corners are the faceting. -->
      <path d="M150.6 60L122.1 77L105.7 98L97.5 119L95.0 140"
            fill="none" stroke="${RED}" stroke-width="2.6" stroke-linejoin="round"/>
      <path d="M199.4 60L227.9 77L244.3 98L252.5 119L255.0 140"
            fill="none" stroke="${RED}" stroke-width="2.6" stroke-linejoin="round"/>

      <g font-family="${MONO}">
        <path d="M292 78h18" stroke="${BLUE}" stroke-width="2.4"/>
        <text x="316" y="82" font-size="11" fill="${BLUE}">oval slices</text>
        <text x="316" y="95" font-size="9" fill="${BLUE}" opacity=".8">cut at intervals</text>

        <path d="M292 126h18" stroke="${RED}" stroke-width="2.6"/>
        <text x="316" y="130" font-size="11" fill="${RED}">swept between</text>
        <text x="316" y="143" font-size="9" fill="${RED}" opacity=".85">straight, so it facets</text>
      </g>
      <text x="175" y="178" text-anchor="middle" font-family="${MONO}" font-size="9"
            fill="${NAVY}" opacity=".55">the widest slice is the brow</text>
    `,
  },

  /** Where the oval was cut off, and what that bought. Two panels, because the
   *  cut and the reason for it are one decision and belong on one card. */
  'crown-cut': {
    title:
      'Two drawings. Above, the modelled dome in profile: only the upper half of ' +
      'the head, sitting on a dashed brow line with an eye on it. About a third of ' +
      'the way up from that line, a dashed black line with a pair of scissors marks ' +
      'where the shell was cut; only the crown above it was printed. Below: a triangle with head ' +
      'coverage and stability at the top vertex and print time and lightweight ' +
      'comfort at the other two, the full-length shell marked high towards coverage ' +
      'and an arrow down to the crown-only shell at the centre of the triangle. It ' +
      'is a schematic of the compromise, not a measurement of it.',
    viewBox: '0 0 460 452',
    markup: `
      <defs>${arrowhead('bm-move', RED)}</defs>

      <!-- ── The cut ──────────────────────────────────────────────────────
           Only the dome is drawn. The head carries on below the brow, but the
           cut is the subject here, and drawing the rest of the head put most of
           the picture below the thing the picture is about. -->

      <!-- Modelled, but below the cut, so not printed. Washed as well as
           outlined: how much of the dome is not in the print is the point. -->
      <path d="M87 150A88 92 0 0 1 92.1 119L257.9 119A88 92 0 0 1 263 150Z"
            fill="${NAVY}" opacity=".05"/>

      <!-- The crown, which is what prints. -->
      <path d="M92.1 119L101.1 100L117.9 80L143.7 64L156.7 60L193.3 60L206.3 64L232.1 80L248.9 100L257.9 119Z"
            fill="${RED}" opacity=".07"/>
      <!-- The true dome, behind the swept form. The gap between the two is the
           whole of the faceting, and it is small. -->
      <path d="M87 150A88 92 0 0 1 263 150" fill="none" stroke="${NAVY}"
            stroke-width="1.6" stroke-dasharray="4 5" opacity=".5"/>
      <g stroke="${BLUE}" stroke-width="1.7" opacity=".7">
        <path d="M101.1 100H248.9"/><path d="M117.9 80H232.1"/><path d="M143.7 64H206.3"/>
      </g>
      <path d="M92.1 119L101.1 100L117.9 80L143.7 64L156.7 60L193.3 60L206.3 64L232.1 80L248.9 100L257.9 119"
            fill="none" stroke="${RED}" stroke-width="2.6" stroke-linejoin="round"/>

      <!-- The brow: the bottom of the dome, and where the eyes are. -->
      <path d="M74 150H276" stroke="${ORANGE}" stroke-width="2" stroke-dasharray="6 5" opacity=".85"/>
      <g opacity=".6">
        <path d="M200 150q13-9 26 0q-13 9-26 0" fill="none" stroke="${NAVY}" stroke-width="1.7"/>
        <circle cx="213" cy="150" r="3.2" fill="${NAVY}"/>
      </g>

      <!-- The cut, above the brow. Run past the dome on both sides so it reads
           as a plane, not as a chord. -->
      <path d="M74 119H276" stroke="${INK}" stroke-width="2.2" stroke-dasharray="9 5"/>
      <g fill="none" stroke="${INK}" stroke-width="1.8" stroke-linecap="round">
        <circle cx="48" cy="112.5" r="3.6"/>
        <circle cx="48" cy="125.5" r="3.6"/>
        <path d="M51.2 114.1L68 124.4"/>
        <path d="M51.2 123.9L68 113.6"/>
      </g>

      <text x="175" y="44" text-anchor="middle" font-family="${MONO}" font-size="10" fill="${RED}">THE PRINTED CROWN</text>

      <g font-family="${MONO}">
        <path d="M292 62h18" stroke="${INK}" stroke-width="2.2" stroke-dasharray="9 5"/>
        <text x="316" y="66" font-size="11" fill="${INK}">where we cut</text>
        <text x="316" y="79" font-size="9" fill="${INK}" opacity=".7">a third up from the brow</text>

        <path d="M292 110h18" stroke="${ORANGE}" stroke-width="2" stroke-dasharray="6 5"/>
        <text x="316" y="114" font-size="11" fill="${ORANGE}">brow line</text>
        <text x="316" y="127" font-size="9" fill="${ORANGE}" opacity=".85">the eyes are here</text>

        <rect x="292" y="152" width="18" height="11" fill="${NAVY}" fill-opacity=".1"
              stroke="${NAVY}" stroke-opacity=".4" stroke-width="1" stroke-dasharray="3 3"/>
        <text x="316" y="162" font-size="11" fill="${NAVY}" opacity=".5">not printed</text>
        <text x="316" y="175" font-size="9" fill="${NAVY}" opacity=".45">modelled, below the cut</text>
      </g>

      <path d="M40 190H420" stroke="${NAVY}" stroke-width="1" opacity=".15"/>

      <!-- ── Why there ────────────────────────────────────────────────────-->
      <g transform="translate(0 204)">
        <path d="M168 93.3L220.6 184.4L115.4 184.4Z" fill="none" stroke="${NAVY}"
              opacity=".16" stroke-width="1.2"/>
        <path d="M168 62L247.7 200L88.3 200Z" fill="${BLUE}" opacity=".05"/>
        <path d="M168 62L247.7 200L88.3 200Z" fill="none" stroke="${NAVY}"
              stroke-width="1.8" stroke-linejoin="round" opacity=".55"/>

        <!-- The option that was not taken. -->
        <circle cx="168" cy="104" r="6.5" fill="#fff" stroke="${NAVY}" stroke-width="2.2" opacity=".7"/>
        <path d="M177 104H286" stroke="${NAVY}" stroke-width="1.2" stroke-dasharray="3 4" opacity=".4"/>
        <path d="M168 115V139" fill="none" stroke="${RED}" stroke-width="1.8"
              stroke-dasharray="5 4" opacity=".7" marker-end="url(#bm-move)"/>
        <!-- The one that was, at the centre of the triangle. -->
        <circle cx="168" cy="154" r="7" fill="${RED}"/>
        <circle cx="168" cy="154" r="11" fill="none" stroke="${RED}" stroke-width="1.4" opacity=".35"/>
        <path d="M182 154H286" stroke="${RED}" stroke-width="1.2" stroke-dasharray="3 4" opacity=".55"/>

        <g font-family="${MONO}" text-anchor="middle">
          <text x="168" y="24" font-size="11" fill="${NAVY}">HEAD COVERAGE</text>
          <text x="168" y="36" font-size="11" fill="${NAVY}">AND STABILITY</text>
          <text x="168" y="48" font-size="8.5" fill="${NAVY}" opacity=".6">more shell, steadier</text>
          <text x="88" y="218" font-size="11" fill="${NAVY}">PRINT TIME</text>
          <text x="88" y="230" font-size="8.5" fill="${NAVY}" opacity=".6">less shell, fewer hours</text>
          <text x="248" y="218" font-size="11" fill="${NAVY}">LIGHTWEIGHT</text>
          <text x="248" y="230" font-size="11" fill="${NAVY}">/ COMFORT</text>
          <text x="248" y="242" font-size="8.5" fill="${NAVY}" opacity=".6">less shell, less to carry</text>
        </g>

        <g font-family="${MONO}">
          <text x="292" y="101" font-size="11" fill="${NAVY}" opacity=".55">full-length shell</text>
          <text x="292" y="114" font-size="9" fill="${NAVY}" opacity=".45">down over the eyes</text>
          <text x="292" y="151" font-size="11" fill="${RED}">crown only</text>
          <text x="292" y="164" font-size="9" fill="${RED}" opacity=".8">what we printed</text>
        </g>
      </g>
    `,
  },

  /** The split, and the reason for it. */
  'two-piece': {
    title:
      'A rough hand-drawn sketch of the mount as two printed parts: a rectangular ' +
      'base plate lifted clear at the top, and a head shell below with three masts ' +
      'standing on its crown. Arrows show the plate dropping onto the masts.',
    viewBox: '0 0 500 280',
    // Drawn as a sketch on purpose: this card is the plan, made before any
    // geometry existed, and a crisp technical ellipse claims a precision the
    // decision never had. Every line is two passes — a firm stroke and a
    // lighter ghost slightly off it — which is what a pen actually leaves.
    markup: `
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <!-- Base plate: a slab in rough perspective, lifted clear -->
        <g stroke="${RED}" stroke-width="2.2">
          <path d="M126 60q108-9 214-4"/>
          <path d="M338 55q15 12 18 27"/>
          <path d="M357 81q-115 11-226 7"/>
          <path d="M130 89q-7-15-4-28"/>
          <!-- thickness -->
          <path d="M131 92q112 8 225 -5"/>
          <path d="M357 84q1 2 0 5"/>
          <path d="M130 89l1 4"/>
        </g>
        <g stroke="${RED}" stroke-width="1.1" opacity=".35">
          <path d="M129 64q106-10 210-6"/>
          <path d="M354 84q-112 9-222 5"/>
        </g>
        <!-- the spindle window, roughed in -->
        <g stroke="${RED}" stroke-width="1.6">
          <path d="M212 68q23-3 46-1"/>
          <path d="M257 66q1 5-1 9"/>
          <path d="M256 75q-22 3-44 1"/>
          <path d="M212 77q-2-5 0-9"/>
        </g>
        <!-- loose hatch on the slab -->
        <g stroke="${RED}" stroke-width="1" opacity=".3">
          <path d="M160 84l14-22M196 84l14-22M312 78l12-19"/>
        </g>

        <!-- Drop arrows, freehand -->
        <g stroke="${BLUE}" stroke-width="2">
          <path d="M152 106q2 11 1 21M231 104q-1 12 0 23M309 106q1 11 1 21"/>
          <path d="M146 121l7 10 7-11M225 122l7 10 7-11M303 121l7 10 7-11"/>
        </g>

        <!-- Head shell: a wobbly dome -->
        <g stroke="${RED}" stroke-width="2.4">
          <path d="M100 224q6-58 52-82q38-20 82-17q46 3 78 26q38 27 42 73"/>
          <path d="M99 224q132 7 256 0"/>
        </g>
        <path d="M104 220q8-52 50-75q39-21 80-17q44 4 76 26q34 25 39 66"
          stroke="${RED}" stroke-width="1.1" opacity=".35"/>
        <g stroke="${RED}" stroke-width="1" opacity=".3">
          <path d="M118 210q4-10 10-18M132 216q5-12 12-21"/>
        </g>

        <!-- Three masts on the crown, open-topped: sockets waiting for the
             plate, with nothing named inside them. -->
        <g stroke="${RED}" stroke-width="2">
          <path d="M147 158q-2-15-1-28M162 157q1-14 0-27M146 131q8-2 16-1"/>
          <path d="M224 146q-1-15 0-29M240 145q1-14 1-28M224 118q8-1 17-1"/>
          <path d="M303 158q-1-14 0-27M318 158q1-14 1-27M303 132q8-2 16-1"/>
        </g>
        <g stroke="${RED}" stroke-width="1" opacity=".35">
          <path d="M150 156q-1-13-1-24M227 144q0-13 0-25M306 156q0-13 0-24"/>
        </g>
      </g>

      <path d="M363 70h20" stroke="${NAVY}" stroke-width="1.4" stroke-dasharray="3 4"/>
      <text x="388" y="62" font-family="${MONO}" font-size="10" fill="${NAVY}">base plate</text>
      <text x="388" y="76" font-family="${MONO}" font-size="9" fill="${NAVY}" opacity=".65">prints flat,</text>
      <text x="388" y="88" font-family="${MONO}" font-size="9" fill="${NAVY}" opacity=".65">no supports</text>

      <path d="M322 143h28" stroke="${NAVY}" stroke-width="1.4" stroke-dasharray="3 4"/>
      <text x="355" y="147" font-family="${MONO}" font-size="10" fill="${NAVY}">masts</text>

      <text x="230" y="248" text-anchor="middle" font-family="${MONO}" font-size="10" fill="${NAVY}">head shell</text>
      <text x="230" y="264" text-anchor="middle" font-family="${MONO}" font-size="9" fill="${NAVY}" opacity=".65">supports on the outside, so the inside stays smooth</text>
    `,
  },
}

export const DIAGRAM_KEYS = Object.keys(DIAGRAMS)

/** The drawing for a key, or null. Built with the DOM parser rather than
 *  innerHTML on a string that could ever come from a field: the markup above is
 *  a constant, and keeping the construction safe keeps it that way. */
export function diagram(key: string): SVGSVGElement | null {
  const spec = DIAGRAMS[key]
  if (!spec) return null

  const ns = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(ns, 'svg')
  svg.setAttribute('class', 'skin-diagram')
  svg.setAttribute('viewBox', spec.viewBox)
  svg.setAttribute('role', 'img')
  const title = document.createElementNS(ns, 'title')
  title.textContent = spec.title
  svg.append(title)

  const parsed = new DOMParser().parseFromString(
    `<svg xmlns="${ns}">${spec.markup}</svg>`,
    'image/svg+xml',
  )
  for (const child of Array.from(parsed.documentElement.childNodes)) {
    svg.append(svg.ownerDocument.importNode(child, true))
  }
  return svg
}

export function diagramTitle(key: string): string {
  return DIAGRAMS[key]?.title ?? ''
}
